/**
 * Hook CRUD pour la table `leave_quotas` + helpers de calcul de soldes.
 *
 * Source de vérité des soldes :
 *   - `leave_quotas` donne le quota (par employé, type, période libre)
 *   - `leaves` (status=validated/pending) donne la consommation
 *   - Le calcul est fait côté client via leaveBalanceUtils pour rester simple
 *     et éviter trigger DB complexe à debugger.
 */
import { useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useToast } from './useToast';
import { useQuery } from './useQuery';
import type { QuotaRecord, LeaveRecord } from '../utils/leaveBalanceUtils';

export interface QuotaInput {
  employee_id: string;
  type: string;
  period_start: string;
  period_end: string;
  total_days: number;
  notes?: string | null;
}

export function useLeaveQuotas() {
  const { user } = useAuth();
  const { toast } = useToast();

  /** Charge tous les quotas (secrétariat a un aperçu global) */
  const {
    data: quotas,
    loading: quotasLoading,
    refresh: refreshQuotas,
  } = useQuery<QuotaRecord>(
    () => supabase
      .from('leave_quotas')
      .select('id, employee_id, type, period_start, period_end, total_days, notes')
      .order('period_start', { ascending: false }),
    [],
  );

  /** Charge tous les leaves (non rejected) pour le calcul des soldes */
  const {
    data: leaves,
    loading: leavesLoading,
    refresh: refreshLeaves,
  } = useQuery<LeaveRecord>(
    () => supabase
      .from('leaves')
      .select('id, employee_id, type, start_date, end_date, status')
      .neq('status', 'rejected'),
    [],
  );

  const createQuota = useCallback(async (input: QuotaInput): Promise<boolean> => {
    if (!user) return false;
    if (input.total_days < 0) {
      toast.warning('Le nombre de jours doit être positif.');
      return false;
    }
    if (input.period_end < input.period_start) {
      toast.warning('La date de fin doit être postérieure à la date de début.');
      return false;
    }

    const { error } = await supabase.from('leave_quotas').insert({
      ...input,
      created_by: user.id,
    });
    if (error) {
      console.error('[useLeaveQuotas] createQuota error:', error);
      // Message parlant si violation d'unicité
      if (error.code === '23505') {
        toast.error('Un quota existe déjà pour cet employé, ce type et cette période.');
      } else {
        toast.error(`Erreur création quota : ${error.message}`);
      }
      return false;
    }
    toast.success('Quota créé.');
    await refreshQuotas();
    return true;
  }, [user, toast, refreshQuotas]);

  const updateQuota = useCallback(async (
    id: string,
    patch: Partial<QuotaInput>,
  ): Promise<boolean> => {
    if (!user) return false;
    if (patch.total_days !== undefined && patch.total_days < 0) {
      toast.warning('Le nombre de jours doit être positif.');
      return false;
    }
    const { error } = await supabase.from('leave_quotas').update(patch).eq('id', id);
    if (error) {
      console.error('[useLeaveQuotas] updateQuota error:', error);
      toast.error(`Erreur modification : ${error.message}`);
      return false;
    }
    toast.success('Quota modifié.');
    await refreshQuotas();
    return true;
  }, [user, toast, refreshQuotas]);

  const deleteQuota = useCallback(async (id: string): Promise<boolean> => {
    if (!user) return false;
    const { error } = await supabase.from('leave_quotas').delete().eq('id', id);
    if (error) {
      console.error('[useLeaveQuotas] deleteQuota error:', error);
      toast.error(`Erreur suppression : ${error.message}`);
      return false;
    }
    toast.success('Quota supprimé.');
    await refreshQuotas();
    return true;
  }, [user, toast, refreshQuotas]);

  return {
    quotas,
    leaves,
    loading: quotasLoading || leavesLoading,
    refreshQuotas,
    refreshLeaves,
    createQuota,
    updateQuota,
    deleteQuota,
  };
}
